import React, { useEffect, useState } from 'react';
import { useAppDispatchActions, useAppState } from '../../context/AppStateProvider/useAppState';
import type { Event } from '../../context/AppStateProvider/types';
import { useApi } from '../../context/ApiProvider/useApi';
import Bookmark from '../Bookmark/Bookmark';
import { Icon } from '../Icons/icons';

type CardProps = {
    event: Event;
};

const Card: React.FC<CardProps> = ({ event }) => {
    const { setActiveEvent } = useAppDispatchActions();
    const { state } = useAppState();
    const api = useApi();
    const actions = useAppDispatchActions();
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        // --- GTM TRACKING START ---
        (window as any).dataCoreLayer = (window as any).dataCoreLayer || [];
        (window as any).dataCoreLayer.push({
            event: 'od_planner_toggle_click_read_more',
            click_text: event.title,
            click_classes: event.area_of_interest.map((interest) => interest.slug).join(' '),
        });
        // --- GTM TRACKING END ---
    }, [isOpen]);

    return (
        <div
            className={`${event.event_status === 'ended' ? 'opacity-50' : ''} mx-auto flex w-full max-w-full cursor-pointer items-start rounded-[16px] bg-[var(--colour-white)] p-[16px] outline outline-[0.75px] outline-offset-[-0.75px] outline-[var(--colour-grey-light-50)] hover:bg-[#F4F4F4]`}
            onClick={() => {
                if (event.event_status !== 'ended') {
                    setActiveEvent(event);
                }
            }}>
            <div className='grid grow grid-cols-[1fr_auto] grid-rows-[auto_auto_auto_auto_auto_auto]'>
                <div className='col-span-2 col-start-1 row-start-1 mb-[12px] flex flex-wrap items-center gap-2'>
                    {event.area_of_interest.map((category) => (
                        <CategoryChip
                            key={category.slug}
                            category={category.slug}
                        />
                    ))}
                </div>
                <h3
                    className='col-span-2 col-start-1 row-start-2 mb-1 text-[16px] leading-4.5 font-bold text-gray-900'
                    dangerouslySetInnerHTML={{ __html: event.title.trim() }}
                />
                <p className='col-span-1 col-start-1 row-start-3 mb-1 text-sm text-gray-700'>
                    {event.time_description} <span className='text-[var(--colour-gold)]'>&nbsp;&bull;&nbsp;</span>{' '}
                    {event.location}
                </p>
                <div className='col-span-2 col-start-1 row-start-4 mb-[24px] flex flex-wrap items-center gap-2 text-sm text-[var(--colour-pale)]'>
                    {event.session_type.map((type) => (
                        <div key={type.slug}>{type.name}</div>
                    ))}
                </div>
                <a
                    className='col-span-1 col-start-1 row-start-5 flex items-center text-sm text-[#3e3e3e] hover:text-[var(--colour-gold)]'
                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        e.stopPropagation();
                        setIsOpen(!isOpen);
                    }}>
                    {isOpen ? (
                        <>
                            {/* Close icon and text when open */}
                            <svg
                                className='mr-1 h-4 w-4'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                                xmlns='http://www.w3.org/2000/svg'>
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth='2'
                                    d='M18 12H6' // horizontal line for minus
                                ></path>
                            </svg>
                            Hide details
                        </>
                    ) : (
                        <>
                            <svg
                                className='mr-1 h-4 w-4'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                                xmlns='http://www.w3.org/2000/svg'>
                                <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth='2'
                                    d='M12 6v6m0 0v6m0-6h6m-6 0H6'></path>
                            </svg>
                            More details
                        </>
                    )}
                </a>
                {isOpen && (
                    <div
                        className='col-span-2 col-start-1 row-start-6 pt-3 text-sm text-[#707070]'
                        dangerouslySetInnerHTML={{ __html: event.content }}
                    />
                )}
                {event.event_status !== 'ended' && (
                    <div className='col-start-2 row-span-3 row-start-3 self-end'>
                        <Bookmark
                            event={event}
                            state={state}
                            api={api}
                            actions={actions}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

const CategoryChip: React.FC<{ category: string }> = ({ category }) => {
    const { state } = useAppState();
    const { interests } = state;

    const interest = interests.find((interest) => interest.slug === category);

    return (
        interest && (
            <div
                style={
                    {
                        '--colour-interest': `var(--colour-interest-${interest.colour})`,
                    } as React.CSSProperties
                }
                className={`inline-flex items-center justify-start gap-1.5 rounded-[12px] bg-[var(--colour-interest)] px-2 py-2`}>
                <div className='relative h-3.5 w-3.5 overflow-hidden'>
                    <div className='absolute top-0 left-[0.17px] h-3.5 w-3.5'>
                        <Icon
                            name={interest.icon ?? 'question'}
                            width={14}
                            height={14}
                        />
                    </div>
                </div>
                <div className='justify-start text-sm text-[14px] leading-4 text-[var(--colour-grey-dark)]'>
                    {interest.name}
                </div>
            </div>
        )
    );
};

export default Card;
